import { useCallback, useMemo } from 'react';
import { buildChatMentionCandidates, normalizeMentionTargetId, normalizeMentionTargetRole, normalizeMentionTargetType } from '../../components/common/chat/mentions/chatMentionUtils';
import { buildGroupArtistSectionsForModal, buildPartsByIdMap } from '../../components/common/chat/members/memberModalSharedDataUtils';
import { buildAdminProfileForModal, buildArtistProfileForModal } from '../../components/common/chat/members/memberModalProfileUtils';
import { resolveGroupAvatarProfileTarget } from '../../components/common/chat/avatars/groupAvatarProfileTargetUtils';
import { parseGroupArtistPartValues, resolveGroupArtistPartValues } from '../../components/common/chat/members/groupArtistPartUtils';
import { buildGroupAdminProfilePayload, buildGroupArtistProfilePayload } from '../../components/common/chat/members/groupProfilePayloadUtils';
import { addAdminGroupChatMembers, deleteAdminGroupChatMember } from '../../api/adminGroupChat';
import { getArtistAvatarStyle, getAdminAvatarStyle, firstNonEmpty } from '../../components/admin/chats/adminGroupChatPageHelpers';

export default function useAdminGroupChatMemberState(context) {
  const {
    addingMembers,
    artistCandidates,
    candidateSelection,
    createArtistCandidates,
    createSelectedArtists,
    loadMembers,
    loadThreadDetail,
    loadThreads,
    loggedInAdminId,
    membersPayload,
    openAdminProfile,
    openArtistProfile,
    openProfileTarget,
    parts,
    removingArtistId,
    selectedThread,
    selectedThreadId,
    setAddingMembers,
    setCandidateSelection,
    setCreateSelectedArtists,
    setRemovingArtistId,
    setShowMembersAddModal,
    setShowMembersModal,
    toastError,
    toastSuccess,
  } = context;

  const memberArtistIds = useMemo(
    () =>
      new Set(
        Array.isArray(membersPayload?.artists)
          ? membersPayload.artists
              .map((row) => String(row?.artist_id || '').trim())
              .filter(Boolean)
          : []
      ),
    [membersPayload]
  );

  const manualCandidateRows = useMemo(
    () =>
      Array.isArray(artistCandidates)
        ? artistCandidates.filter((row) => {
            const artistId = String(row?.artist_id || '').trim();
            return artistId && !memberArtistIds.has(artistId);
          })
        : [],
    [artistCandidates, memberArtistIds]
  );

  const partMetaMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(parts)) return map;
    parts.forEach((part, index) => {
      const normalizedKeys = [part?.value, part?.id, part?.part_id, part?.short_name, part?.label, part?.name]
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      if (!normalizedKeys.length) return;
      const canonicalKey = normalizedKeys[0];
      const shortLabel = String(part?.short_name || part?.shortName || part?.label || canonicalKey).trim() || canonicalKey;
      const label = String(part?.label || part?.name || part?.short_name || canonicalKey).trim() || canonicalKey;
      const orderRaw = Number(part?.order ?? part?.sort_order);
      const order = Number.isFinite(orderRaw) ? orderRaw : index;
      normalizedKeys.forEach((key) => {
        if (!map.has(key)) {
          map.set(key, {
            key: canonicalKey,
            shortLabel,
            label,
            order,
          });
        }
      });
    });
    return map;
  }, [parts]);

  const getArtistPartValues = useCallback((artist) => {
    return resolveGroupArtistPartValues(artist);
  }, []);

  const resolveArtistPrimaryPart = useCallback(
    (artist) => {
      const partValues = getArtistPartValues(artist);
      const primaryPart = partValues[0] || '';
      if (!primaryPart) {
        return {
          key: '__unassigned__',
          label: '未設定',
          shortLabel: '',
          order: Number.MAX_SAFE_INTEGER,
        };
      }
      const meta = partMetaMap.get(primaryPart);
      if (!meta) {
        const fallbackLabel = firstNonEmpty(
          artist?.part_label,
          artist?.main_part_label,
          artist?.partLabel,
          artist?.mainPartLabel,
          artist?.part_short_name,
          artist?.main_part_short_name,
          artist?.partShortName,
          artist?.mainPartShortName,
        );
        const fallbackShortLabel = firstNonEmpty(
          artist?.part_short_name,
          artist?.main_part_short_name,
          artist?.partShortName,
          artist?.mainPartShortName,
          artist?.part_label,
          artist?.main_part_label,
          artist?.partLabel,
          artist?.mainPartLabel,
        );
        return {
          key: primaryPart,
          label: fallbackLabel || primaryPart,
          shortLabel: fallbackShortLabel || fallbackLabel || primaryPart,
          order: Number.isFinite(Number(artist?.part_sort_order ?? artist?.partSortOrder))
            ? Number(artist?.part_sort_order ?? artist?.partSortOrder)
            : Number.MAX_SAFE_INTEGER - 1,
        };
      }
      return meta;
    },
    [getArtistPartValues, partMetaMap]
  );

  const resolveArtistShortPart = useCallback(
    (artist) => {
      const primary = resolveArtistPrimaryPart(artist);
      return String(primary?.shortLabel || '').trim();
    },
    [resolveArtistPrimaryPart]
  );

  const buildArtistProfileData = useCallback(
    (artist) => {
      if (!artist) return null;
      return buildArtistProfileForModal({
        artist: buildGroupArtistProfilePayload({
          source: artist,
          pickFirstString: firstNonEmpty,
          resolvePartLabel: (source) => {
            const resolvedPart = resolveArtistPrimaryPart(source);
            return firstNonEmpty(
              source.partLabel,
              source.mainPartLabel,
              source.part,
              source.mainPart,
              resolvedPart?.label === '未設定' ? '' : resolvedPart?.label,
            );
          },
          defaultName: '名称未設定',
        }),
      });
    },
    [resolveArtistPrimaryPart]
  );

  const buildAdminProfileData = useCallback((admin) => {
    if (!admin) return null;
    return buildAdminProfileForModal({
      admin: buildGroupAdminProfilePayload({
        source: admin,
        pickFirstString: firstNonEmpty,
        defaultName: '管理者',
      }),
    });
  }, []);
  const formatAdminNameForMemberPeople = useCallback(
    (admin) => firstNonEmpty(admin?.full_name, admin?.username, admin?.name) || '管理者',
    []
  );
  const adminInitialForMemberPeople = useCallback(
    (name) => String(name || 'A').trim().charAt(0) || 'A',
    []
  );
  const adminAvatarStyleForMemberPeople = useCallback(
    (pic) =>
      getAdminAvatarStyle({
        profile_picture: pic,
        admin_profile_picture: pic,
        picture: pic,
        pic,
      }),
    []
  );
  const artistAvatarStyleForMemberPeople = useCallback(
    (pic, gender) =>
      getArtistAvatarStyle({
        profile_picture: pic,
        artist_profile_picture: pic,
        pic,
        gender,
      }),
    []
  );
  const artistAvatarInitialForMemberPeople = useCallback(
    (name) => String(name || '?').trim().charAt(0) || '?',
    []
  );

  const partsByIdForGrouping = useMemo(() => buildPartsByIdMap(parts), [parts]);

  const groupArtistsByPrimaryPart = useCallback(
    (artists = []) => {
      if (!Array.isArray(artists) || artists.length === 0) return [];
      const sections = buildGroupArtistSectionsForModal({
        artists,
        partsById: partsByIdForGrouping,
        parsePartIds: parseGroupArtistPartValues,
        pickFirstStringFn: firstNonEmpty,
        resolvePartIdsFn: getArtistPartValues,
      });
      return sections.map((section) => ({
        key: section.id,
        label: section.title || '未設定',
        order: Number.isFinite(section.order) ? section.order : Number.MAX_SAFE_INTEGER,
        members: Array.isArray(section.members) ? section.members : [],
      }));
    },
    [getArtistPartValues, partsByIdForGrouping]
  );

  const groupedMemberArtists = useMemo(() => {
    const artists = Array.isArray(membersPayload?.artists) ? membersPayload.artists : [];
    return groupArtistsByPrimaryPart(artists);
  }, [groupArtistsByPrimaryPart, membersPayload?.artists]);

  const groupedMemberArtistSections = useMemo(
    () =>
      groupedMemberArtists.map((partGroup) => ({
        id: String(partGroup?.key || ''),
        title: String(partGroup?.label || '未設定'),
        titleShort: '',
        order: partGroup?.order,
        members: Array.isArray(partGroup?.members) ? partGroup.members : [],
      })),
    [groupedMemberArtists]
  );

  const groupedSidePanelArtists = useMemo(() => {
    const artists = Array.isArray(selectedThread?.artists) ? selectedThread.artists : [];
    return groupArtistsByPrimaryPart(artists);
  }, [groupArtistsByPrimaryPart, selectedThread?.artists]);

  const assigneeLookup = useMemo(() => {
    const map = new Map();
    const list = [
      ...(Array.isArray(selectedThread?.assignees) ? selectedThread.assignees : []),
      ...(Array.isArray(membersPayload?.assignees) ? membersPayload.assignees : []),
    ];
    list.forEach((admin) => {
      const key = String(
        admin?.id ||
          admin?.admin_user_id ||
          admin?.admin_id ||
          admin?.user_id ||
          ''
      ).trim();
      if (key && !map.has(key)) {
        map.set(key, admin);
      }
    });
    return map;
  }, [membersPayload?.assignees, selectedThread?.assignees]);

  const artistLookup = useMemo(() => {
    const map = new Map();
    const list = [
      ...(Array.isArray(selectedThread?.artists) ? selectedThread.artists : []),
      ...(Array.isArray(membersPayload?.artists) ? membersPayload.artists : []),
    ];
    list.forEach((artist) => {
      const key = String(
        artist?.id ||
          artist?.artist_id ||
          artist?.artist_user_id ||
          artist?.user_id ||
          ''
      ).trim();
      if (key && !map.has(key)) {
        map.set(key, artist);
      }
    });
    return map;
  }, [membersPayload?.artists, selectedThread?.artists]);

  const mentionCandidates = useMemo(() => buildChatMentionCandidates({
    admins: Array.from(assigneeLookup.values()),
    artists: Array.from(artistLookup.values()),
    parts,
    currentUser: { role: 'admin', id: loggedInAdminId },
  }), [artistLookup, assigneeLookup, loggedInAdminId, parts]);

  const openMessageAvatarProfile = useCallback(
    (message) => {
      const target = resolveGroupAvatarProfileTarget({
        message,
        artistLookup,
        assigneeLookup,
        buildArtistProfile: buildArtistProfileData,
        buildAdminProfile: buildAdminProfileData,
      });
      openProfileTarget(target);
    },
    [artistLookup, assigneeLookup, buildAdminProfileData, buildArtistProfileData, openProfileTarget]
  );

  const handleMentionClick = useCallback(
    (mention) => {
      const mentionType = normalizeMentionTargetType(mention);
      if (mentionType === 'part') {
        setShowMembersModal(true);
        setShowMembersAddModal(false);
        return;
      }

      const role = normalizeMentionTargetRole(mention);
      const targetId = normalizeMentionTargetId(mention);
      const artist = artistLookup.get(targetId);
      const admin = assigneeLookup.get(targetId);
      if (role === 'admin' && admin) {
        openAdminProfile(buildAdminProfileData(admin));
        return;
      }
      if (role === 'artist' && artist) {
        openArtistProfile(buildArtistProfileData(artist));
        return;
      }
      if (artist) openArtistProfile(buildArtistProfileData(artist));
      else if (admin) openAdminProfile(buildAdminProfileData(admin));
    },
    [
      artistLookup,
      assigneeLookup,
      buildAdminProfileData,
      buildArtistProfileData,
      openAdminProfile,
      openArtistProfile,
      setShowMembersAddModal,
      setShowMembersModal,
    ]
  );

  const toggleCandidate = useCallback((artistId) => {
    const normalized = String(artistId || '').trim();
    if (!normalized) return;
    setCandidateSelection((current) =>
      current.includes(normalized)
        ? current.filter((value) => value !== normalized)
        : [...current, normalized]
    );
  }, [setCandidateSelection]);

  const addAllMemberCandidates = useCallback(() => {
    setCandidateSelection((current) => {
      const seen = new Set(current);
      const next = [...current];
      manualCandidateRows.forEach((artist) => {
        const artistId = String(artist?.artist_id || '').trim();
        if (!artistId || seen.has(artistId)) return;
        seen.add(artistId);
        next.push(artistId);
      });
      return next;
    });
  }, [manualCandidateRows, setCandidateSelection]);

  const handleAddMembers = useCallback(async () => {
    if (!selectedThreadId || !candidateSelection.length || addingMembers) return;
    setAddingMembers(true);
    try {
      await addAdminGroupChatMembers(String(selectedThreadId), {
        artistIds: candidateSelection,
      });
      setCandidateSelection([]);
      await Promise.all([
        loadMembers(selectedThreadId),
        loadThreadDetail(selectedThreadId),
        loadThreads({ silent: true }),
      ]);
      toastSuccess('メンバーを追加しました');
      setShowMembersAddModal(false);
    } catch (error) {
      toastError(error?.message || 'メンバー追加に失敗しました');
    } finally {
      setAddingMembers(false);
    }
  }, [
    addingMembers,
    candidateSelection,
    loadMembers,
    loadThreadDetail,
    loadThreads,
    selectedThreadId,
    setAddingMembers,
    setCandidateSelection,
    setShowMembersAddModal,
    toastError,
    toastSuccess,
  ]);

  const handleRemoveMember = useCallback(
    async (artistId) => {
      const normalized = String(artistId || '').trim();
      if (!selectedThreadId || !normalized || removingArtistId) return;
      setRemovingArtistId(normalized);
      try {
        await deleteAdminGroupChatMember(String(selectedThreadId), normalized);
        await Promise.all([
          loadMembers(selectedThreadId),
          loadThreadDetail(selectedThreadId),
          loadThreads({ silent: true }),
        ]);
        toastSuccess('メンバーを削除しました');
      } catch (error) {
        toastError(error?.message || 'メンバー削除に失敗しました');
      } finally {
        setRemovingArtistId('');
      }
    },
    [
      loadMembers,
      loadThreadDetail,
      loadThreads,
    removingArtistId,
    selectedThreadId,
    setRemovingArtistId,
    toastError,
    toastSuccess,
  ]
  );

  const createSelectedArtistIdSet = useMemo(
    () =>
      new Set(
        createSelectedArtists
          .map((artist) => String(artist?.artist_id || '').trim())
          .filter(Boolean)
      ),
    [createSelectedArtists]
  );

  const addCreateArtist = useCallback((artist) => {
    const artistId = String(artist?.artist_id || '').trim();
    if (!artistId) return;
    setCreateSelectedArtists((current) => {
      if (current.some((row) => String(row?.artist_id || '').trim() === artistId)) return current;
      return [...current, artist];
    });
  }, [setCreateSelectedArtists]);

  const removeCreateArtist = useCallback((artistId) => {
    const normalized = String(artistId || '').trim();
    if (!normalized) return;
    setCreateSelectedArtists((current) =>
      current.filter((artist) => String(artist?.artist_id || '').trim() !== normalized)
    );
  }, [setCreateSelectedArtists]);

  const toggleCreateArtist = useCallback(
    (artist) => {
      const artistId = String(artist?.artist_id || '').trim();
      if (!artistId) return;
      if (createSelectedArtistIdSet.has(artistId)) {
        removeCreateArtist(artistId);
      } else {
        addCreateArtist(artist);
      }
    },
    [addCreateArtist, createSelectedArtistIdSet, removeCreateArtist]
  );

  const addAllCreateCandidates = useCallback(() => {
    setCreateSelectedArtists((current) => {
      const seen = new Set(current.map((artist) => String(artist?.artist_id || '').trim()).filter(Boolean));
      const next = [...current];
      createArtistCandidates.forEach((artist) => {
        const artistId = String(artist?.artist_id || '').trim();
        if (!artistId || seen.has(artistId)) return;
        seen.add(artistId);
        next.push(artist);
      });
      return next;
    });
  }, [createArtistCandidates, setCreateSelectedArtists]);

  return {
    memberArtistIds, manualCandidateRows, partMetaMap, getArtistPartValues, resolveArtistPrimaryPart, resolveArtistShortPart,
    buildArtistProfileData, buildAdminProfileData, formatAdminNameForMemberPeople, adminInitialForMemberPeople, adminAvatarStyleForMemberPeople, artistAvatarStyleForMemberPeople,
    artistAvatarInitialForMemberPeople, partsByIdForGrouping, groupArtistsByPrimaryPart, groupedMemberArtists, groupedMemberArtistSections, groupedSidePanelArtists,
    assigneeLookup, artistLookup, mentionCandidates, openMessageAvatarProfile, handleMentionClick, toggleCandidate,
    addAllMemberCandidates, handleAddMembers, handleRemoveMember, createSelectedArtistIdSet, addCreateArtist, removeCreateArtist,
    toggleCreateArtist, addAllCreateCandidates,
  };
}
